/**
 * <sticky-cta>
 *
 * Reveals once the press-logo-marquee section enters the viewport — i.e.
 * once the user has scrolled past the buy box into the section below it.
 * Stays visible while the user continues down the page; hides again only
 * if the user scrolls back ABOVE the logo section (back up to the buy
 * box / hero). Clicking the sticky ATC smooth-scrolls the user back to
 * the top of the buy box (purchase-matrix) to finalize their selection.
 */
(() => {
  if (customElements.get('sticky-cta')) return;

  // Overlay UI the corner-widget scan must never touch: panels that own their
  // own `transform` for a slide-in, where an inline translateY() would destroy
  // the animation and throw the panel off screen (see _findCornerWidgets).
  // sections/main-product-pdp.liquid exempts the same UI from its Videowise
  // hider for the same reason — keep the two in step.
  //
  // Panel roots — matched with closest(), so descendants are covered too.
  // Every entry here is a real overlay container; none can ever be <html> or
  // <body>, which matters because closest() walks the whole ancestor chain.
  const OVERLAY_ROOT_SEL = [
    'drawer-root',
    '.ocu-cart-drawer',
    '.ocu-cart-overlay',
    '[aria-modal="true"]',
  ].join(', ');

  // Catch-all for any other OCU/Zipify-owned node, matched against the element
  // ITSELF only. Deliberately not part of the closest() list: while their cart
  // is open Zipify puts `ocu-overflow-hidden` on <html>, so a closest() lookup
  // for `[class*="ocu-"]` would match every element on the page and quietly
  // switch the whole corner-widget feature off.
  const OVERLAY_SELF_SEL = [
    '[class*="ocu-"]',
    '[id*="ocu-"]',
    '[class*="zipify"]',
    '[id*="zipify"]',
  ].join(', ');

  class StickyCta extends HTMLElement {
    connectedCallback() {
      // Portal to <body> so no ancestor (Shopify section wrapper, theme layout
      // div, etc.) can ever introduce a transform / filter / will-change /
      // contain that would re-anchor our `position: fixed` to a non-viewport
      // containing block. Bulletproof against future ancestor styling changes.
      if (this.parentNode !== document.body) {
        document.body.appendChild(this);
      }

      this.atcButton = this.querySelector('[data-role="sticky-atc"]');

      this._lastVisible = null;   // what is currently APPLIED
      this._scrollShow = false;   // what the scroll position alone wants
      this._cartOpen = false;     // a cart/drawer overlay is up — bar stands down
      this._pendingRaf = null;

      this.watchCartState();
      this.observeLogoSection();
      this.bindAtc();
    }

    /**
     * An open cart drawer covers the full height of the viewport and owns the
     * shopper's attention. Leaving the bar up puts a second, competing CTA over
     * the drawer and buries the drawer's own checkout button — scrolling the
     * cart never reveals it, because the bar is pinned above. So the bar stands
     * down for as long as any drawer is open, then returns to whatever the
     * scroll position had decided.
     */
    isCartOpen() {
      const panel = document.querySelector('.ocu-cart-drawer.ocu-visible');
      if (panel && getComputedStyle(panel).visibility !== 'hidden') return true;
      // Zipify's scroll lock outlives a half-finished open, so this also covers
      // the case where their panel never painted — the shopper is still stuck
      // behind an overlay either way, and the bar must not be the thing on top.
      if (document.documentElement.classList.contains('ocu-overflow-hidden')) return true;
      const native = document.querySelector('drawer-root');
      if (native && getComputedStyle(native).display !== 'none') return true;
      return false;
    }

    watchCartState() {
      const refresh = () => {
        const open = this.isCartOpen();
        if (open === this._cartOpen) return;
        this._cartOpen = open;
        this.applyVisible();
      };
      this._cartOpen = this.isCartOpen();

      // OCU toggles `ocu-overflow-hidden` on <html> around every open/close and
      // the theme drawer flips its own display — watching those attributes is
      // cheap (three nodes, no subtree) and catches both drawers. Zipify's
      // lifecycle events are layered on top rather than relied on: the 2026-08-21
      // incident showed their cascade can die before emitting them.
      const observer = new MutationObserver(refresh);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      const native = document.querySelector('drawer-root');
      if (native) observer.observe(native, { attributes: true, attributeFilter: ['class', 'style', 'open'] });

      ['ocu:cart:opened', 'ocu:cart:close', 'ocu:cart:update'].forEach((evt) => {
        addEventListener(evt, () => setTimeout(refresh, 50));
      });
    }

    applyVisible() {
      const show = this._scrollShow && !this._cartOpen;
      if (show === this._lastVisible) return;
      this._lastVisible = show;
      this.dataset.visible = String(show);
      this.setAttribute('aria-hidden', String(!show));
      this._publishVisibleState(show);
    }

    observeLogoSection() {
      // Reveal trigger (QA launch fix): watch the buy-box ATC form itself,
      // not the press marquee. On short pages (mobile especially) the
      // marquee could enter the viewport while the buy box was still
      // on-screen, so the bar appeared ON TOP of purchase content. Watching
      // the ATC form means the bar only shows once the real buy button has
      // scrolled up out of view — "take me back" is only offered when the
      // thing to go back to is actually gone. Falls back to the marquee on
      // templates without a purchase matrix.
      const target = document.querySelector('purchase-matrix [data-role="atc-form"]')
        || document.querySelector('purchase-matrix')
        || document.querySelector('.salt-press-marquee');
      if (!target) {
        // Target absent (non-PDP context) — keep the sticky hidden rather
        // than fall back to always-on behavior.
        this.setAttribute('aria-hidden', 'true');
        this.dataset.visible = 'false';
        return;
      }
      // Show once the target has scrolled ABOVE the viewport (bottom < 0):
      // the shopper is past the buy box. Hide whenever any part of it is
      // back on screen or below (scrolling back up).
      const io = new IntersectionObserver((entries) => {
        const last = entries[entries.length - 1];
        const show = !last.isIntersecting && last.boundingClientRect.bottom < 0;
        if (show === this._scrollShow) return;
        if (this._pendingRaf) cancelAnimationFrame(this._pendingRaf);
        this._pendingRaf = requestAnimationFrame(() => {
          this._pendingRaf = null;
          this._scrollShow = show;
          // applyVisible(), not a direct write — an open cart drawer overrides
          // whatever the scroll position wants.
          this.applyVisible();
        });
      }, { threshold: 0 });
      io.observe(target);
    }

    // Expose the bar's visible state + measured height to the rest of the
    // page, and lift any bottom-pinned floating widgets (Videowise's
    // Stories Bubble, chat tools, etc.) up so they don't overlap the bar.
    // We can't rely on a CSS class match because third-party widgets ship
    // wildly different DOM across versions — instead we DOM-scan for any
    // `position: fixed` element that's anchored to the bottom of the
    // viewport but isn't a full-width banner, then directly set a
    // `transform: translateY()` on it. Reverted on hide.
    _publishVisibleState(show) {
      document.body.classList.toggle('salt-sticky-cta-visible', show);
      if (show) {
        const h = this.offsetHeight;
        if (h > 0) {
          document.documentElement.style.setProperty('--salt-sticky-cta-height', `${h}px`);
          // Clearance (QA launch fix): pad the page bottom by the bar's
          // height while it's up, so a bottom-pinned bar can never cover
          // the last content on short pages (mobile Seedless/Sensitive).
          if (this.classList.contains('salt-sticky-cta--bottom')) {
            document.body.style.paddingBottom = `${h}px`;
          }
        }
      } else {
        document.body.style.paddingBottom = '';
      }
      this._offsetCornerWidgets(show);
    }

    _offsetCornerWidgets(show) {
      if (!this._trackedWidgets) this._trackedWidgets = new Map();

      if (show) {
        this._applyOffsetToCornerWidgets();
        // Catch late-loading widgets (Videowise + chat tools often hydrate
        // a second or two after page load). Watch body for new children
        // while the bar is visible; re-apply the offset to any fresh ones.
        if (!this._widgetObserver) {
          this._widgetObserver = new MutationObserver(() => {
            if (this._lastVisible) this._applyOffsetToCornerWidgets();
          });
          this._widgetObserver.observe(document.body, { childList: true, subtree: true });
        }
      } else {
        this._trackedWidgets.forEach((originalTransform, el) => {
          el.style.transform = originalTransform;
        });
        this._trackedWidgets.clear();
        if (this._widgetObserver) {
          this._widgetObserver.disconnect();
          this._widgetObserver = null;
        }
      }
    }

    _applyOffsetToCornerWidgets() {
      const offset = this.offsetHeight + 16;
      this._findCornerWidgets().forEach((el) => {
        if (!this._trackedWidgets.has(el)) {
          // Save the original inline transform so we can restore it on hide.
          // (Widget's own JS may set transforms during enter animations —
          // we only override the steady-state value while sticky is up.)
          this._trackedWidgets.set(el, el.style.transform || '');
          el.style.transition = 'transform 0.3s ease';
        }
        el.style.transform = `translateY(-${offset}px)`;
      });
    }

    _findCornerWidgets() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const matches = [];
      // Scan every fixed-positioned element under <body>. Videowise nests
      // its floating bubble two levels deep on some versions, so we can't
      // limit to direct body children. The querySelectorAll is broad but
      // each getComputedStyle short-circuits quickly on non-fixed elements.
      const all = document.body.querySelectorAll('*');
      for (const el of all) {
        if (el === this || this.contains(el)) continue;
        // Never claim cart/drawer UI. Zipify's OCU drawer matched every test
        // below while open — fixed, 400px wide (under the 85vw banner cutoff),
        // full height so its bottom edge sits at the viewport floor — so the
        // bar "lifted" it with an inline translateY(). That inline transform
        // beats the `transform: translate(-400px)` slide-in Zipify applies from
        // its stylesheet, so the panel snapped back to its parked `right:-400px`
        // and left the screen entirely, while the full-width `.ocu-cart-overlay`
        // sailed past the 85vw check and stayed up: cart flashes in, jumps away,
        // dark screen with no panel and the page still scroll-locked. Reported
        // on the recommendation-card ATCs specifically because those sit far
        // enough down the page that the bar is already showing when the drawer
        // opens — up in the buy box the bar is hidden and nothing touches it.
        if (el.closest(OVERLAY_ROOT_SEL) || el.matches(OVERLAY_SELF_SEL)) continue;
        const style = getComputedStyle(el);
        if (style.position !== 'fixed') continue;
        if (style.display === 'none' || style.visibility === 'hidden') continue;
        const rect = el.getBoundingClientRect();
        // Must be a real on-screen rectangle.
        if (rect.width < 60 || rect.height < 60) continue;
        // Skip full-width bars (cookie banners, announcement strips) —
        // a corner widget is, by definition, narrower than the viewport.
        if (rect.width > vw * 0.85) continue;
        // Must be anchored to the bottom portion of the viewport so we
        // only catch things the sticky bar would actually collide with.
        if (rect.bottom < vh - 200) continue;
        matches.push(el);
      }
      // De-dup nested matches — if both a parent and a descendant are
      // fixed-positioned, only offset the outermost one (otherwise the
      // child gets translated twice, ending up off-screen).
      return matches.filter((el) => !matches.some((other) => other !== el && other.contains(el)));
    }

    bindAtc() {
      if (!this.atcButton) return;
      this.atcButton.addEventListener('click', (e) => {
        e.preventDefault();
        // Send the user back to the top of the buy box ("Select Quantity")
        // instead of submitting the form. The sticky bar shows only after
        // the buy-box ATC has scrolled off — clicking BUY NOW now means
        // "take me back to make my selection", not "buy whatever's default".
        // The bar auto-hides once the buy-box ATC re-enters the viewport.
        const target = document.querySelector('purchase-matrix');
        if (!target) return;

        // Anchor the target just below the sticky header. Reading
        // header-root.getBoundingClientRect().bottom already accounts for the
        // announcement bar's sticky offset when both are pinned, so we don't
        // need to sum heights ourselves.
        const header = document.querySelector('header-root');
        const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const targetTop = target.getBoundingClientRect().top + window.scrollY - headerBottom - 16;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      });
    }
  }

  customElements.define('sticky-cta', StickyCta);
})();
